import { defineStore } from 'pinia';
import TaskStatus from '@/enums/TaskStatus';
import ITaskItem from '@/interfaces/ITaskItem';
import createTask from '@/factories/createTaskFactory';
import { generateNewId } from '@/helpers/idHelpers';
import { getDetailsFromError } from '@/helpers/errorHelpers';

export const useTaskStore = defineStore('task', {
  state: () => ({
    tasks: [] as ITaskItem[],
  }),

  getters: {
    totalTasks: (state): number => {
      return state.tasks.length;
    },

    hasAllTasksCompleted: (state): boolean => {
      return state.tasks.every(
        (task) =>
          task.status !== TaskStatus.IN_PROGRESS &&
          task.status !== TaskStatus.WAITING
      );
    },

    // a function to know if there are any tasks in progress or waiting
    hasTasksInProgressOrWaiting: (state): boolean => {
      return state.tasks.some(
        (task) =>
          task.status === TaskStatus.IN_PROGRESS ||
          task.status === TaskStatus.WAITING
      );
    },
  },

  actions: {
    addTask(title: string, description?: string): number {
      const newId = generateNewId(this.tasks.map((task) => task.id));
      const newTask = createTask();
      newTask.id = newId;
      newTask.title = title;
      newTask.description = description || '';
      this.tasks.push(newTask);
      return newId;
    },

    startTask(taskId: number) {
      const task = this.findTaskByID(taskId);
      if (task) {
        task.status = TaskStatus.IN_PROGRESS;
        task.startedAt = new Date();
        this.updateTaskById(taskId, task);
      }
    },
    
    /**
     * Finish a task with the success status
     * @param taskId
     * @param response 
     */
    finishTaskSuccess(taskId: number, response?: string[]) {
      this.finishTask(taskId, TaskStatus.SUCCESS, response);
    },
    
    /**
     * Finish a task with the error status
     * @param taskId 
     * @param response 
     */
    finishTaskError(taskId: number, response?: string[]) {
      this.finishTask(taskId, TaskStatus.ERROR, response);
    },

    /**
     * Finish a task with the warning status
     * @param taskId 
     * @param response 
     */
    finishTaskWarning(taskId: number, response?: string[]) {
      this.finishTask(taskId, TaskStatus.WARNING, response);
    },

    removeTask(ID: number): boolean {
      // Find the task by ID
      const index = this.tasks.findIndex((task) => task.id === ID);
      // If the task is found, remove it and return true
      if (index !== -1) {
        this.tasks.splice(index, 1);
        return true;
      }
      // Return false if the task is not found
      return false;
    },

    updateTaskById(ID: number, updatedTask: ITaskItem): boolean {
      // Find the task by ID
      const index = this.tasks.findIndex((task) => task.id === ID);
      // If the task is found, update it and return true
      if (index !== -1) {
        this.tasks.splice(index, 1, updatedTask);
        return true;
      }
      return false;
    },
    findTaskByID(ID: number): ITaskItem | undefined {
      // Find the task by ID and return
      return this.tasks.find((task) => task.id === ID);
    },
    finishTask(taskId: number, status: TaskStatus, response?: string[]) {
      // Find the task by ID
      const task = this.findTaskByID(taskId);
      // If the task is found, set the status, finishedAt and response
      if (task) {
        task.status = status;
        task.finishedAt = new Date();
        task.response = response;
        // Update the task
        this.updateTaskById(taskId, task);
      }
    },

    /**
     * 
     * @param taskId 
     * @param error ES5 Error object with optional cause property
     * @param response 
     */
    finishTaskWithError(taskId: number, error: Error, response?: string[]) {
      // Get the error message from the error object
      const ErrorMessage = getDetailsFromError(error);
      // Setup internal response array
      const Responses: string[] = response || [];
      // If ErrorMessage is not empty, add it to the response array at the end
      if(ErrorMessage!=''){
        Responses.push(ErrorMessage);
      }
      //Finish the task with the error status and the response array
      this.finishTask(taskId, TaskStatus.ERROR, Responses);
    },

    addAndStartTask(title: string, description?: string): number {
      // Add a new task
      const newId = this.addTask(title, description);
      // Start the task
      this.startTask(newId);
      // Return the ID of the new task
      return newId;
    },

    clearAll(): boolean {
      //empty the task array
      this.tasks = [];
      // return true if the array is empty
      return this.tasks.length === 0;
    },
  },
});
